/**
 * VisitJoinPage: the WeaveIn join link for a virtual calendar event (TRUS-688).
 *
 * Events with no external meeting provider no longer get a public meet.jit.si
 * room. Their link is {origin}/visit/{eventId}, which opens this page. The link
 * itself grants nothing; the API decides who may join:
 *
 * - Staff (signed in): the API says what the event is.
 *   - A telehealth visit opens the visit's video room.
 *   - A WeaveIn-hosted meeting connects here, with a token only the organizer
 *     and staff attendees can get, inside the join window.
 *   - An event on a configured Zoom / Meet / Teams link goes to that link.
 * - Members join their visit from the WeaveIn app (Telehealth), signed in as
 *   themselves. The web page tells them so; it never asks for or shows a member id.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Video, VideoOff, Mic, MicOff, PhoneOff, AlertCircle, Smartphone, LogIn } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { meetingAPI, type EventJoinInfo, type EventVideoToken } from '@/services/api/calendar';
import useTwilioVideo from '@/hooks/useTwilioVideo';
import TwilioParticipant from '@/components/telehealth/TwilioParticipant';

/** sessionStorage flag: the visitor chose "I'm staff, sign in" on this page. */
export const VISIT_JOIN_STAFF_SIGNIN_KEY = 'visit_join_staff_signin';

interface VisitJoinPageProps {
  eventId: number;
  isAuthenticated: boolean;
  authLoading?: boolean;
}

/** Parse /visit/{id} from a pathname; null when it is not a join link. */
export function parseVisitPath(pathname: string): number | null {
  const m = /^\/visit\/(\d+)\/?$/.exec(pathname);
  return m ? Number(m[1]) : null;
}

/** Open the telehealth visit in the main app (the coordinator's video room). */
function openTelehealthSession(sessionId: number) {
  try {
    localStorage.setItem('telehealth_session_id', String(sessionId));
    localStorage.setItem('activeTab', 'telehealth-session');
  } catch {
    /* storage blocked: the dashboard still opens */
  }
  window.location.replace('/');
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow p-6 text-center">{children}</div>
    </div>
  );
}

function SignedOut() {
  const { t } = useAppTranslation();
  return (
    <Shell>
      <Video className="h-10 w-10 text-teal-600 mx-auto mb-3" aria-hidden />
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('calendar.visitJoin.title', 'Join your video visit')}
      </h1>
      <div className="text-start rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100 mb-1">
          <Smartphone className="h-4 w-4" aria-hidden />
          {t('calendar.visitJoin.memberHeading', 'Joining as a member?')}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t(
            'calendar.visitJoin.memberSteps',
            'Open the WeaveIn app, sign in, and go to More, then Telehealth. Your visit opens there 15 minutes before it starts.'
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          try {
            sessionStorage.setItem(VISIT_JOIN_STAFF_SIGNIN_KEY, '1');
          } catch {
            /* ignore */
          }
          window.location.reload();
        }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700"
      >
        <LogIn className="h-4 w-4" aria-hidden />
        {t('calendar.visitJoin.staffSignIn', 'Staff: sign in to join')}
      </button>
    </Shell>
  );
}

function Message({ title, body }: { title: string; body?: string }) {
  const { t } = useAppTranslation();
  return (
    <Shell>
      <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" aria-hidden />
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h1>
      {body && <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{body}</p>}
      <button
        type="button"
        onClick={() => window.location.replace('/')}
        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
      >
        {t('calendar.visitJoin.backToApp', 'Back to WeaveIn')}
      </button>
    </Shell>
  );
}

function MeetingRoom({ grant, title, onLeave }: { grant: EventVideoToken; title?: string | null; onLeave: () => void }) {
  const { t } = useAppTranslation();
  const [error, setError] = useState<string | null>(null);
  const video = useTwilioVideo({
    token: grant.token,
    roomName: grant.room_name,
    onError: (e) => setError(e.message),
    enableNetworkQuality: true,
  });
  const { connect, isConnected, isConnecting, error: connectError } = video;

  useEffect(() => {
    if (!isConnected && !isConnecting && !connectError) void connect();
  }, [connect, isConnected, isConnecting, connectError]);

  const leave = () => {
    video.disconnect();
    onLeave();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <header className="px-4 py-3 flex items-center justify-between">
        <span className="font-medium truncate">{title || t('calendar.visitJoin.meeting', 'Meeting')}</span>
        {isConnecting && <Loader2 className="h-4 w-4 animate-spin" aria-label={t('calendar.visitJoin.connecting', 'Connecting')} />}
      </header>
      {error && <p className="px-4 text-sm text-red-300">{error}</p>}
      <main className="flex-1 grid gap-3 p-3 grid-cols-1 sm:grid-cols-2">
        {video.localParticipant && <TwilioParticipant participant={video.localParticipant} isLocal />}
        {video.remoteParticipants.map((p) => (
          <TwilioParticipant key={p.sid} participant={p} isDominantSpeaker={video.dominantSpeaker?.sid === p.sid} />
        ))}
        {isConnected && video.remoteParticipants.length === 0 && (
          <div className="flex items-center justify-center text-gray-400 text-sm">
            {t('calendar.visitJoin.waitingForOthers', 'Waiting for others to join')}
          </div>
        )}
      </main>
      <footer className="flex items-center justify-center gap-3 p-4">
        <button
          type="button"
          onClick={video.toggleAudio}
          className="p-3 rounded-full bg-gray-700 hover:bg-gray-600"
          aria-label={video.isAudioEnabled ? t('calendar.visitJoin.mute', 'Mute') : t('calendar.visitJoin.unmute', 'Unmute')}
        >
          {video.isAudioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </button>
        <button
          type="button"
          onClick={video.toggleVideo}
          className="p-3 rounded-full bg-gray-700 hover:bg-gray-600"
          aria-label={
            video.isVideoEnabled ? t('calendar.visitJoin.cameraOff', 'Turn camera off') : t('calendar.visitJoin.cameraOn', 'Turn camera on')
          }
        >
          {video.isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </button>
        <button
          type="button"
          onClick={leave}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700"
          aria-label={t('calendar.visitJoin.leave', 'Leave')}
        >
          <PhoneOff className="h-5 w-5" />
        </button>
      </footer>
    </div>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'not-open'; info: EventJoinInfo }
  | { kind: 'no-link' }
  | { kind: 'room'; info: EventJoinInfo; grant: EventVideoToken }
  | { kind: 'left' }
  | { kind: 'error' };

export default function VisitJoinPage({ eventId, isAuthenticated, authLoading = false }: VisitJoinPageProps) {
  const { t } = useAppTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });

  const resolve = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const info = await meetingAPI.getEventJoinInfo(eventId);
      if (info.kind === 'telehealth' && info.telehealth_session_id) {
        openTelehealthSession(info.telehealth_session_id);
        return;
      }
      if (info.kind === 'external') {
        if (info.join_url) window.location.replace(info.join_url);
        else setState({ kind: 'no-link' });
        return;
      }
      if (!info.is_participant) {
        setState({ kind: 'not-found' });
        return;
      }
      if (!info.can_join_now) {
        setState({ kind: 'not-open', info });
        return;
      }
      const grant = await meetingAPI.getEventVideoToken(eventId);
      setState({ kind: 'room', info, grant });
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setState({ kind: status === 404 || status === 403 ? 'not-found' : 'error' });
    }
  }, [eventId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      sessionStorage.removeItem(VISIT_JOIN_STAFF_SIGNIN_KEY);
    } catch {
      /* ignore */
    }
    void resolve();
  }, [isAuthenticated, resolve]);

  if (authLoading) {
    return (
      <Shell>
        <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto" aria-label={t('calendar.visitJoin.loading', 'Loading')} />
      </Shell>
    );
  }
  if (!isAuthenticated) return <SignedOut />;

  switch (state.kind) {
    case 'loading':
      return (
        <Shell>
          <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto" aria-label={t('calendar.visitJoin.loading', 'Loading')} />
        </Shell>
      );
    case 'room':
      return <MeetingRoom grant={state.grant} title={state.info.title} onLeave={() => setState({ kind: 'left' })} />;
    case 'not-open': {
      const opens = state.info.join_opens_at ? new Date(state.info.join_opens_at) : null;
      return (
        <Message
          title={t('calendar.visitJoin.notOpenTitle', 'This meeting is not open right now')}
          body={
            opens && opens.getTime() > Date.now()
              ? t('calendar.visitJoin.opensAt', 'You can join from {{time}}.', { time: opens.toLocaleString() })
              : t('calendar.visitJoin.ended', 'This meeting has ended.')
          }
        />
      );
    }
    case 'no-link':
      return <Message title={t('calendar.visitJoin.noLink', 'This event has no video link')} />;
    case 'left':
      return <Message title={t('calendar.visitJoin.leftTitle', 'You left the meeting')} />;
    case 'not-found':
      return (
        <Message
          title={t('calendar.visitJoin.notFoundTitle', 'Meeting not available')}
          body={t(
            'calendar.visitJoin.notFoundBody',
            'This link is not available to your account. If you were invited, ask the organizer to add you.'
          )}
        />
      );
    default:
      return (
        <Message
          title={t('calendar.visitJoin.errorTitle', 'Could not open the meeting')}
          body={t('calendar.visitJoin.errorBody', 'Please try again in a moment.')}
        />
      );
  }
}
